import React, { useEffect, useMemo } from "react"

import { PayerCoverageCheckNetwork } from "@common-shared/config/coverage.config.ts"
import { QuotaUsageType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config.ts"

import {
    CoverageCheckerMode,
    CoverageCheckerRunningState,
} from "../../../config/checker.config"
import { useAppDispatch } from "../../../store/appDispatch.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { checkerSelector } from "../../../store/selectors/checker.selector"
import { checkerSetRunningState } from "../../../store/slices/checker.slice"
import type { ICommonCheckResult } from "../../../types/objects.types.ts"
import { CoverageResultComponent } from "../result/coverageResult.component"

export const CheckerResultsComponent: React.FunctionComponent<{
    className?: string
}> = props => {
    const dispatch = useAppDispatch()

    const {
        checkerMode,
        continuousMonitoringCoverageChecks,
        manualCheckType,
        medicaidChecks,
    } = useAppSelector(checkerSelector)

    const neededResults = useMemo<(ICommonCheckResult | null)[]>(() => {
        const mappedContinuousMonitoringResults =
            continuousMonitoringCoverageChecks.map(item => item.result || null)

        const mappedMedicaidResults = medicaidChecks.map(check => {
            if (!check.result) {
                return null
            }

            return {
                // Medicaid is always in network
                [PayerCoverageCheckNetwork.OUT]: null,
                [PayerCoverageCheckNetwork.IN]: check.result,
            }
        })

        switch (manualCheckType) {
            case QuotaUsageType.MEDICAID:
                return mappedMedicaidResults

            case QuotaUsageType.GENERAL:
            case QuotaUsageType.DISCOVER:
            case QuotaUsageType.SMART_SCAN:
            case QuotaUsageType.AGENTIC_PORTAL:
            case QuotaUsageType.AGENTIC_PHONE:
                return mappedContinuousMonitoringResults

            default:
                return mappedContinuousMonitoringResults.length
                    ? mappedContinuousMonitoringResults
                    : mappedMedicaidResults.length
                      ? mappedMedicaidResults
                      : []
        }
    }, [manualCheckType, continuousMonitoringCoverageChecks, medicaidChecks])

    // Wrong data in state
    useEffect(() => {
        if (
            continuousMonitoringCoverageChecks.length ||
            medicaidChecks.length
        ) {
            return
        }

        dispatch(
            checkerSetRunningState(
                checkerMode === CoverageCheckerMode.MANUAL
                    ? CoverageCheckerRunningState.INPUT_MANUAL
                    : CoverageCheckerRunningState.INPUT_CSV,
            ),
        )
    }, [checkerMode, continuousMonitoringCoverageChecks, medicaidChecks])

    return (
        <div
            className={`
                relative
                ${props.className}
            `}
        >
            {neededResults.map((item, index) => {
                return item ? (
                    <CoverageResultComponent key={index} results={item} />
                ) : null
            })}
        </div>
    )
}
